import { useState, useEffect, useRef } from "react";

import { isNotEmpty } from "neetocist";
import { isEmpty } from "ramda";

const useProductSwitcherNavigation = (filteredApps = []) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => setHighlightedIndex(0), [filteredApps]);

  useEffect(() => {
    if (!scrollContainerRef.current || isEmpty(filteredApps)) return;

    const container = scrollContainerRef.current;
    const highlightedElement = container.querySelector(
      `[data-highlighted-index="${highlightedIndex}"]`
    );

    if (!highlightedElement) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = highlightedElement.getBoundingClientRect();

    if (elRect.top < containerRect.top) {
      container.scrollTop += elRect.top - containerRect.top;
    } else if (elRect.bottom > containerRect.bottom) {
      container.scrollTop += elRect.bottom - containerRect.bottom;
    }
  }, [highlightedIndex, filteredApps]);

  const getColumnsPerRow = () => {
    const width = window.innerWidth;

    if (width < 768) return 1;

    if (width < 1024) return 2;

    if (width < 1536) return 3;

    return 4;
  };

  const handleOpenHighlightedApp = () => {
    const app = filteredApps[highlightedIndex];
    if (!app?.url) return;

    window.open(app.url, "_blank", "noopener,noreferrer");
  };

  const navigationHandlers = {
    left: prev => (prev === 0 ? filteredApps.length - 1 : prev - 1),
    right: prev => (prev === filteredApps.length - 1 ? 0 : prev + 1),
    up: prev => {
      const columnsPerRow = getColumnsPerRow();
      const newIndex = prev - columnsPerRow;
      if (newIndex >= 0) return newIndex;

      const currentCol = prev % columnsPerRow;
      const lastIndex = filteredApps.length - 1;

      return lastIndex - ((lastIndex - currentCol) % columnsPerRow);
    },
    down: prev => {
      const columnsPerRow = getColumnsPerRow();
      const newIndex = prev + columnsPerRow;
      if (newIndex < filteredApps.length) return newIndex;

      return prev % columnsPerRow;
    },
  };

  const keyActions = {
    Enter: handleOpenHighlightedApp,
    ArrowLeft: () => setHighlightedIndex(navigationHandlers["left"]),
    ArrowRight: () => setHighlightedIndex(navigationHandlers["right"]),
    ArrowUp: () => setHighlightedIndex(navigationHandlers["up"]),
    ArrowDown: () => setHighlightedIndex(navigationHandlers["down"]),
    Tab: e =>
      setHighlightedIndex(navigationHandlers[e.shiftKey ? "left" : "right"]),
  };

  const handleKeyDown = e => {
    const action = keyActions[e.key];
    if (!action) return;

    e.preventDefault();
    e.stopPropagation();

    if (isNotEmpty(filteredApps)) action(e);
  };

  return { highlightedIndex, handleKeyDown, scrollContainerRef };
};

export default useProductSwitcherNavigation;
